import { isAxiosError } from 'axios';

export const CAPTURE_WRITE_TIMEOUT_MS = 20_000;

// A rejected tool is NOT a 4xx on the wire: /protected/tools/execute answers 200 with
// { status: 'FAILED', error }, and assertToolSucceeded turns that into a plain Error. So a plain
// Error always means the server answered and decided — never retry it. Only an AxiosError with no
// response (drop or timeout) or a 5xx is ambiguous about whether the write landed.
export function isAmbiguousTransportFailure(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  if (error.response === undefined) return true;
  return error.response.status >= 500;
}

export type CaptureWriteOutcome<T> = { kind: 'written'; result: T } | { kind: 'already-present' };

export interface CaptureWriteOptions<T> {
  label: string;
  write: () => Promise<T>;
  alreadyWritten?: () => Promise<boolean>;
  priorAttemptWasAmbiguous?: boolean;
}

// The precheck reads the render over the same connection the write just failed on, so it will
// usually fail too when the network is down. A precheck that cannot answer means "unknown", and
// unknown proceeds to the write — aborting here would lose the one retry and would surface the
// render's error instead of the save's.
async function isAlreadyWritten(
  label: string,
  alreadyWritten: (() => Promise<boolean>) | undefined
): Promise<boolean> {
  if (alreadyWritten === undefined) return false;
  try {
    return await alreadyWritten();
  } catch (error) {
    console.warn('[capture-write] duplicate precheck failed, proceeding', label, error);
    return false;
  }
}

export async function runCaptureWrite<T>(options: CaptureWriteOptions<T>): Promise<CaptureWriteOutcome<T>> {
  const { label, write, alreadyWritten, priorAttemptWasAmbiguous = false } = options;

  if (priorAttemptWasAmbiguous && (await isAlreadyWritten(label, alreadyWritten))) {
    console.warn('[capture-write] already present after an ambiguous failure, skipping write', label);
    return { kind: 'already-present' };
  }

  try {
    return { kind: 'written', result: await write() };
  } catch (error) {
    if (!isAmbiguousTransportFailure(error)) {
      console.error('[capture-write] failed', label, error);
      throw error;
    }
    console.warn('[capture-write] ambiguous failure, retrying once', label, error);
    if (await isAlreadyWritten(label, alreadyWritten)) {
      console.warn('[capture-write] already present after an ambiguous failure, skipping retry', label);
      return { kind: 'already-present' };
    }
    try {
      return { kind: 'written', result: await write() };
    } catch (retryError) {
      console.error('[capture-write] failed after one retry', label, retryError);
      throw retryError;
    }
  }
}
